import * as Yup from 'yup';
import Link from 'next/link';
import { useFormik } from 'formik';
import React, { useEffect } from 'react';

import { TextInput, SubmitButton } from '../form-elements';
import { ResetPasswordInputs, ResetPasswordErrors } from '../../../redux/auth/interfaces';
import { withAnalytics, WithAnalyticsProps } from '../../../HOC/with-analytics';

type ResetPasswordFormProps = {
  errors: ResetPasswordErrors;
  isResetting?: boolean;
  resetPassword: (data: ResetPasswordInputs) => void;
} & WithAnalyticsProps;

const ResetPasswordForm: React.SFC<ResetPasswordFormProps> = (props) => {
  const initialValues: ResetPasswordInputs = {
    password: '',
    confirmPassword: '',
  };

  const formik = useFormik({
    initialValues,
    onSubmit: (values) => {
      props.resetPassword({ ...values });
    },
    validationSchema: Yup.object().shape({
      password: Yup.string()
        .required('Password is required')
        .min(8, 'Password must be contain atleast 8 characters')
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#£\$%\^&\*])(?=.{8,})/,
          'Password must be contain atleast One Uppercase letter, One Lowercase letter, One Number and One special character. Accepted characters are ! @ # £ $ % & * ^',
        ),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Confirm Password is required'),
    }),
  });

  const _handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    props.sendAnalytics('clickResetPassword', 'Auth');
    formik.submitForm();
  };

  useEffect(() => {
    if (!props.isResetting) {
      formik.setSubmitting(false);
      if (Object.keys(props.errors || {}).length > 0) formik.setErrors(props.errors);
    }
  }, [props.isResetting]);

  const { values, errors, touched, isSubmitting, ...formProps } = formik;

  return (
    <div className="max-w-md mx-auto px-4">
      <p className="text-header text-center text-3xl sm:text-4xl leading-tight tracking-wide font-bold">
        Forgot Password?
      </p>

      <p className="text-center font-normal text-sm lg:font-medium mt-1 sm:text-lg py-2 mb-6">
        No worries, let us help you get back in.
      </p>

      <form onSubmit={_handleSubmit}>
        <div className="form-row">
          <TextInput
            type="password"
            name="password"
            value={values.password}
            errors={errors.password}
            placeholder="New Password"
            handleBlur={formProps.handleBlur}
            handleChange={formProps.handleChange}
            hasError={errors.password && touched.password}
          />
        </div>

        <div className="form-row mt-2">
          <TextInput
            type="password"
            name="confirmPassword"
            value={values.confirmPassword}
            errors={errors.confirmPassword}
            placeholder="Confirm New Password"
            handleBlur={formProps.handleBlur}
            handleChange={formProps.handleChange}
            hasError={errors.confirmPassword && touched.confirmPassword}
          />
        </div>

        <p className="mt-4 text-sm">
          I remember my Password now? {''}
          <Link href="/signin">
            <a className="text-secondary">Let me Sign In</a>
          </Link>
        </p>

        {(errors as ResetPasswordErrors).submit && (
          <div className="p-3 text-red-600 font-light bg-red-300 rounded text-sm my-4">
            {(errors as ResetPasswordErrors).submit}
          </div>
        )}

        <SubmitButton
          direction="right"
          title="Reset Password"
          isDirty={formProps.dirty}
          loadingMsg="Resetting..."
          isSubmitting={isSubmitting}
          className="mt-6"
        />
      </form>
    </div>
  );
};

export default withAnalytics(ResetPasswordForm);
